import React, { useState, useEffect } from 'react';
import CustomCard from '../CustomCard';
import SecondaryButton from '../SecondaryButton';
import PrimaryButton from '../PrimaryButton';
import { Box, Typography, useMediaQuery, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatPhoneNumber } from '../../utils/formatPhoneNumber';
import { PastTour } from '../../types/tours';
import ProfileCardOverlay from '../ProfileCardOverlay';
import axiosClient from '../../services/axiosClient';
import { trackFeatureUsage } from '../../services/FeatureTrackingService';
import { useAuth } from '../../hooks/useAuth';

interface PastTourCardProps {
  past_tour: PastTour;
}

const PastTourCard: React.FC<PastTourCardProps> = ({ past_tour }) => {
  const { user } = useAuth();
  const initial = past_tour.prospect_name?.[0]?.toUpperCase() || '?';
  const isMobile = useMediaQuery('(max-width:600px)');
  const [showDetails, setShowDetails] = useState(false);
  // Task creation state
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [taskCreated, setTaskCreated] = useState(false);
  const [isCheckingTask, setIsCheckingTask] = useState(true);

  // Check if task already exists when component loads
  useEffect(() => {
    const checkExistingTask = async () => {
      if (!past_tour.prospect_id || !past_tour.listing_id) {
        setIsCheckingTask(false);
        return;
      }

      try {
        const response = await axiosClient.get('/tasks/check_send_app_task_exists', {
          params: {
            listing_id: past_tour.listing_id,
            prospect_id: past_tour.prospect_id
          }
        });
        
        if (response.data.task_exists) {
          setTaskCreated(true);
        }
      } catch (error) {
        console.error('Failed to check task existence:', error);
      } finally {
        setIsCheckingTask(false);
      }
    };

    checkExistingTask();
  }, [past_tour.prospect_id, past_tour.listing_id]);
  
  const handleShowDetails = () => {
    // Track feature usage
    if (user?.id) {
      trackFeatureUsage(user.id, 'See Details');
    }
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
  };

  // Send application task creation for a prospect
  const handleSendApplication = async () => {
    if (!past_tour.prospect_id || !past_tour.listing_id) {
      return;
    }

    try {
      setIsCreatingTask(true);
      const response = await axiosClient.post('/tasks/create_send_application_task', {

        listing_id: past_tour.listing_id,
        prospect_id: past_tour.prospect_id
      });
      
      if (response.data.success) {
        // Track feature usage
        if (user?.id) {
          trackFeatureUsage(user.id, 'Send Application');
        }
        setTaskCreated(true);
      }
    } catch (error: any) {
      // If task already exists, mark it as created
      if (error.response?.data?.task_exists) {
        setTaskCreated(true);
      }
    } finally {
      setIsCreatingTask(false);
    }
  };

  return (
    <>
    <CustomCard sx={{ p: 2, maxWidth: '90%' }}>
      <Box display="flex" alignItems="flex-start" mb={1.5}>
        {/* Avatar */}
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: '#FFEB3B',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 500,
            fontSize: 24,
            color: '#222',
            mr: 2,
            flexShrink: 0,
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {initial}
        </Box>
        {/* Details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={700} sx={{ fontSize: 17, mb: 0.2, fontFamily: 'Inter, sans-serif' }}>
            {past_tour.prospect_name}
          </Typography>

          <Typography variant="body2" color="text.primary" sx={{ fontSize: 14, fontWeight: 400, mt:0.2, mb: 0.2, fontFamily: 'Inter, sans-serif' }}>
            Tour completed on {past_tour.date} at {past_tour.start_time_local}
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 400, mb: 0.2, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: 220, fontFamily: 'Inter, sans-serif' }}>
            {past_tour.address}
          </Typography>
          {/* Passed screening */}
          {past_tour.passed_screening === true && (
            <Box display="flex" alignItems="center" mt={0.5}>
              <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontFamily: 'Inter, sans-serif' }}>
                Passed AI screening
              </Typography>
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18, ml: 0.5 }} />
            </Box>
          )}

        </Box>
      </Box>
      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <SecondaryButton 
          fullWidth 
          sx={{ fontWeight: 600, fontSize: 15 }}
          onClick={handleShowDetails}
        >
          See details
        </SecondaryButton>
      </Box>
    </CustomCard>

    {showDetails && past_tour.prospect && (
      <ProfileCardOverlay
        prospect={past_tour.prospect}
        onClose={handleCloseDetails}
      />
    )}
    </>
  );
};

export default PastTourCard;